import { FilterOperatorType } from '../components/odata-filter/models/filter-operator-type';
import { OPERATORS } from '../components/odata-filter/models/operators';
import {
  endOfLastWeek,
  endOfWeek,
  getDate,
  startOfLastWeek,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  startOfLastMonth,
  endOfLastMonth,
  yesterday,
  thisYear,
  lastYear,
  today,
} from '../utils/date-utils';

export class ODataFilterBuilder {
  static buildSimpleFilter(
    targetField: string,
    searchValue: any,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false
  ): any {
    if (
      filterOperatorType == FilterOperatorType.eq ||
      filterOperatorType == FilterOperatorType.ne ||
      filterOperatorType == FilterOperatorType.gt ||
      filterOperatorType == FilterOperatorType.lt ||
      filterOperatorType == FilterOperatorType.ge ||
      filterOperatorType == FilterOperatorType.le ||
      filterOperatorType == FilterOperatorType.startswith ||
      filterOperatorType == FilterOperatorType.endswith ||
      filterOperatorType == FilterOperatorType.contains
    ) {
      if (convertToNotOperator && filterOperatorType == FilterOperatorType.ne) {
        return { not: { [targetField]: { [FilterOperatorType.eq]: searchValue } } };
      }
      return { [targetField]: { [filterOperatorType]: searchValue } };
    } else {
      return ['Invalid Operation'];
    }
  }
  static buildStringFilter(
    targetField: string,
    searchValue: string,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false
  ): any {
    let isValidOperator =
      OPERATORS.String.operators.find((x) => x.actualOperator == filterOperatorType) != null;
    if (!isValidOperator) {
      return ['Invalid Operation'];
    }

    let _searchValue = searchValue ? (searchValue as string).toLowerCase().trim() : null;
    if (convertToNotOperator && filterOperatorType == FilterOperatorType.ne) {
      return { not: { [`tolower(${targetField})`]: { [FilterOperatorType.eq]: _searchValue } } };
    }
    return { [`tolower(${targetField})`]: { [filterOperatorType]: _searchValue } };
  }

  static buildEmailFilter(
    targetField: string,
    searchValue: string,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false
  ): any {
    let isValidOperator =
      OPERATORS.Email.operators.find((x) => x.actualOperator == filterOperatorType) != null;
    if (!isValidOperator) {
      return ['Invalid Operation'];
    }
    let _searchValue: string | string[];
    if (filterOperatorType == FilterOperatorType.in) {
      _searchValue = searchValue
        ? (searchValue as string)
            .toLowerCase()
            .trim()
            .split(';')
            .map((value) => value.trim())
            .filter((value) => value != '')
        : [];
    } else {
      _searchValue = searchValue ? (searchValue as string).toLowerCase().trim() : null;
    }
    if (convertToNotOperator && filterOperatorType == FilterOperatorType.ne) {
      return { not: { [`tolower(${targetField})`]: { [FilterOperatorType.eq]: _searchValue } } };
    }
    return { [`tolower(${targetField})`]: { [filterOperatorType]: _searchValue } };
  }

  static buildNumberFilter(
    targetField: string,
    searchValue: string,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false
  ): any {
    let isValidOperator =
      OPERATORS.Number.operators.find((x) => x.actualOperator == filterOperatorType) != null;
    if (!isValidOperator) {
      return ['Invalid Operation'];
    }

    let _searchValue = searchValue != null ? Number.parseFloat(searchValue) : null;
    if (convertToNotOperator && filterOperatorType == FilterOperatorType.ne) {
      return { not: { [targetField]: { [FilterOperatorType.eq]: _searchValue } } };
    }
    return { [targetField]: { [filterOperatorType]: _searchValue } };
  }
  static buildGuidFilter(
    targetField: string,
    searchValue: string,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false
  ): any {
    if (
      filterOperatorType != FilterOperatorType.eq &&
      filterOperatorType != FilterOperatorType.ne
    ) {
      return ['Invalid Operation'];
    }
    if (convertToNotOperator && filterOperatorType == FilterOperatorType.ne) {
      return {
        not: { [targetField]: { [FilterOperatorType.eq]: { type: 'guid', value: searchValue } } },
      };
    }
    return { [targetField]: { [filterOperatorType]: { type: 'guid', value: searchValue } } };
  }

  static buildArrayFilter(
    targetField: string,
    searchValue: string[],
    filterOperatorType: FilterOperatorType = FilterOperatorType.in,
    convertToNotOperator: boolean = false
  ) {
    if (filterOperatorType == FilterOperatorType.isEmpty) {
      return { [targetField]: { eq: null } };
    } else if (filterOperatorType == FilterOperatorType.isNotEmpty) {
      return convertToNotOperator
        ? { not: { [targetField]: { eq: null } } }
        : { [targetField]: { ne: null } };
    } else if (filterOperatorType == FilterOperatorType.in) {
      return { [targetField]: { in: searchValue } };
    } else {
      return ['Invalid Operation'];
    }
  }

  static buildDateFilter(
    targetField: string,
    searchValue: Date,
    filterOperatorType: FilterOperatorType = FilterOperatorType.eq,
    convertToNotOperator: boolean = false,
    buildForAutomation: boolean = false
  ): any {
    let isValidOperator =
      OPERATORS.Date.operators.find((x) => x.displayOperator == filterOperatorType) != null;

    if (!isValidOperator) {
      return ['Invalid Operation'];
    }
    if (filterOperatorType == FilterOperatorType.isEmpty) {
      return { [targetField]: { eq: { type: 'raw', value: null } } };
    }
    if (filterOperatorType == FilterOperatorType.isNotEmpty) {
      return convertToNotOperator
        ? { not: { [targetField]: { eq: { type: 'raw', value: null } } } }
        : { [targetField]: { ne: { type: 'raw', value: null } } };
    }

    if (
      filterOperatorType == FilterOperatorType.eq ||
      filterOperatorType == FilterOperatorType.lt ||
      filterOperatorType == FilterOperatorType.gt
    ) {
      return {
        [targetField]: {
          [filterOperatorType]: {
            type: 'raw',
            value: getDate(searchValue),
          },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.today) {
      return {
        [targetField]: {
          eq: {
            type: 'raw',
            value: today(buildForAutomation),
          },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.yesterday) {
      return {
        [targetField]: {
          eq: {
            type: 'raw',
            value: yesterday(buildForAutomation),
          },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.onOrBeforeToday) {
      return {
        [targetField]: {
          le: {
            type: 'raw',
            value: today(buildForAutomation),
          },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.onOrAfterToday) {
      return {
        [targetField]: {
          ge: {
            type: 'raw',
            value: today(buildForAutomation),
          },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.thisYear) {
      return {
        [`year(${targetField})`]: {
          eq: { type: 'raw', value: thisYear(buildForAutomation) },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.lastYear) {
      return {
        [`year(${targetField})`]: {
          eq: { type: 'raw', value: lastYear(buildForAutomation) },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.thisWeek) {
      return {
        [targetField]: {
          ge: { type: 'raw', value: startOfWeek(buildForAutomation) },
          le: { type: 'raw', value: endOfWeek(buildForAutomation) },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.lastWeek) {
      return {
        [targetField]: {
          ge: { type: 'raw', value: startOfLastWeek(buildForAutomation) },
          le: { type: 'raw', value: endOfLastWeek(buildForAutomation) },
        },
      };
    }

    if (filterOperatorType == FilterOperatorType.thisMonth) {
      return {
        [targetField]: {
          ge: { type: 'raw', value: startOfMonth(buildForAutomation) },
          le: { type: 'raw', value: endOfMonth(buildForAutomation) },
        },
      };
    }
    if (filterOperatorType == FilterOperatorType.lastMonth) {
      return {
        [targetField]: {
          ge: { type: 'raw', value: startOfLastMonth(new Date(), buildForAutomation) },
          le: { type: 'raw', value: endOfLastMonth(new Date(), buildForAutomation) },
        },
      };
    }
  }

  static buildDateIsNotExpiredFilter(targetField: string) {
    let _value = new Date();
    _value.setDate(_value.getDate() - 1);
    return {
      or: [
        {
          [targetField]: {
            gt: {
              type: 'raw',
              value: getDate(_value),
            },
          },
        },
        {
          [targetField]: null,
        },
      ],
    };
  }

  static buildDateRangeFilter(
    targetField1: string,
    targetField2: string,
    searchValue: Date,
    filterOperatorType: FilterOperatorType,
    buildForAutomation: boolean = false
  ) {
    if (filterOperatorType == FilterOperatorType.isCurrentlyActive) {
      return {
        and: [
          {
            [targetField1]: {
              le: {
                type: 'raw',
                value: today(buildForAutomation),
              },
            },
          },
          {
            or: [
              {
                [targetField2]: {
                  ge: {
                    type: 'raw',
                    value: today(buildForAutomation),
                  },
                },
              },
              {
                [targetField2]: {
                  eq: {
                    type: 'raw',
                    value: null,
                  },
                },
              },
            ],
          },
        ],
      };
    }

    if (filterOperatorType == FilterOperatorType.isNotCurrentlyActive) {
      return {
        or: [
          {
            [targetField1]: {
              gt: {
                type: 'raw',
                value: today(buildForAutomation),
              },
            },
          },
          {
            [targetField2]: {
              lt: {
                type: 'raw',
                value: today(buildForAutomation),
              },
            },
          },
        ],
      };
    }

    if (filterOperatorType == FilterOperatorType.activeOn) {
      return {
        and: [
          {
            [targetField1]: {
              le: {
                type: 'raw',
                value: getDate(searchValue),
              },
            },
          },
          {
            or: [
              {
                [targetField2]: {
                  ge: {
                    type: 'raw',
                    value: getDate(searchValue),
                  },
                },
              },
              {
                [targetField2]: {
                  eq: {
                    type: 'raw',
                    value: null,
                  },
                },
              },
            ],
          },
        ],
      };
    }

    return ['Invalid Operation'];
  }
}
